<template>
  <b-overlay :show="loading">
    <div class="pt-2 pb-2 pl-2 pr-2 border card shadow" style="border-radius:10px; font-weight: 600;">
      <div class="pb-1" sty>
        Report - Patients Seen
      </div>

      <b-row>
        <b-col md="3">
          <b-form-datepicker
            v-model="dateStart"
            :close-button="true"
            reset-button
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            placeholder="From"
            class="mr-2"
            @input="(val) => dateStart = val"
          />
        </b-col>

        <b-col md="3">
          <b-form-datepicker
            v-model="dateEnd"
            placeholder="To"
            reset-button
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            :min="dateStart"
            @input="(val) => dateEnd = val"
          />
        </b-col>

        <b-col md="3">
          <v-select 
            v-model="gender" 
            placeholder="Gender"
            :options="[{label: 'Male', value: 'male'}, { label: 'Female', value: 'female'} ]" 
            :clearable="true" 
            :close-on-select="true"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
            @input="(val) => gender = val ? val.value: '' "
          >
          </v-select>
        </b-col>

        <b-col md="3">
          <v-select 
            v-model="insurance_partner" 
            placeholder="Insurance Partner"
            :options="[
              {label: 'Ahotopa Comfort', value: 'Ahotopa Comfort'}, 
              { label: 'Ahotopa with MTN', value: 'Ahotopa with MTN'},
              { label: 'Premier Health', value: 'Premier Health'},
              { label: 'Teledokta', value: 'Teledokta'},
              ]" 
            :clearable="true" 
            :close-on-select="true"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
            @input="(val) => insurance_partner = val ? val.value : ''"
          >
          </v-select>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="3">
          <v-select 
            v-model="ageRange" 
            placeholder="Age Range"
            :options="['0-9', '10-19', '20-29', '30-39', '40-49', '50+']" 
            :clearable="true" 
            :close-on-select="true"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" 
            @input="(val) => ageRange = val"
          >
          </v-select>
        </b-col>
      </b-row>
    </div>

    <div>
      <b-row>
        <b-col cols="12">
          <patient-by-region-chart :key="regionKey" :series="chartSeries" :chart-options="chartOptions"></patient-by-region-chart>
        </b-col>
      </b-row>

      <b-row class="match-height">
        <b-col md="6">
          <patient-by-age-group-chart :series="ageGroupSeries" :chart-options="ageGroupOptions"></patient-by-age-group-chart>
        </b-col>

        <b-col md="6">
          <patient-by-gender-chart :series="genderChartSeries" :chart-options="genderChartOptions"></patient-by-gender-chart>
        </b-col>
      </b-row>
    </div>

    <div>
      <list-table
        :records="patientsData" 
        :columns="tableColumns"
        :show-import-button="false"
        :show-export-button="can('patients_seen_reports:CanExport')"
        :show-search="false"
        :total-records="totalRecords"
        :current-page-number.sync="currentPage"
        @export="onExport"
      >
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import PatientByAgeGroupChart from "@core/components/shared/admin/reports/view/AgeGroupChart.vue";
import PatientByGenderChart from "@core/components/shared/admin/reports/view/GenderChart.vue";
import PatientByRegionChart from "@core/components/shared/admin/reports/view/RegionChart.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import vSelect from 'vue-select';
import FileDownload from "js-file-download";

import { startOfMonth } from 'date-fns';
import { formatDate } from "@/@core/utils/utils";
import { debounce, get, startCase } from "lodash";
import { BAlert, BAvatar, BBadge, BCard, BCardImg, BCol, BFormDatepicker, BImg, BLink, BOverlay, BRow } from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  },
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const chartColors = [
  'rgba(7, 125, 188, 1)',
  'rgba(101, 163, 13, 1)',
  'rgba(7, 125, 188, 0.7)',
  'rgba(101, 163, 13, 0.7)',
  'rgba(75, 75, 75, 0.2)',
  'rgba(7, 125, 188, 0.6)',
  'rgba(101, 163, 13, 0.6)',
  'rgba(7, 125, 188, 0.4)',
  'rgba(101, 163, 13, 0.4)',
  'rgba(7, 125, 188, 0.2)'
]

export default {
  components: {
  BCardImg,
  BOverlay,
  BAlert,
  BAvatar,
  BLink,
  BImg,
  BBadge,
  BRow,
  BCol,
  BCard,
  vSelect,
  ListTable,
  BFormDatepicker,
  PatientByRegionChart,
  PatientByGenderChart,
  PatientByAgeGroupChart,
},
data(){
  const chartOptions = {
      chart: {
        height: 400,
        type: 'area',
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        }
      },
      dataLabels: {
        enabled: false,
        formatter: (value) => (value),
      },
      colors: chartColors,
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        lines: {
          show: false
        },
        type: 'string',
        categories: []
      },
      yaxis: {
        lines: {
          show: false
        },
        labels: {
          formatter: (value) => value
        }
      },
      tooltip: {
        x: {
          format: 'MM/yy'
        },
        y: {
          formatter: (value) => value
        }
      },
      legend: {
        position: "top",
        horizontalAlign: 'left'
      }
  }
  const monthBeginning = startOfMonth(new Date())
  return {
    chartOptions,
    loading: false,

    minAge: "",
    maxAge: "",
    ageRange: "",
    gender: "",
    dateStart: formatDate(monthBeginning, 'yyyy-MM-dd'),
    dateEnd: formatDate(new Date(), 'yyyy-MM-dd'),

    regionKey: 99,
    patientsData: [],
    insurance_partner: null,
    
    chartSeries: [],
    ageGroupOptions: {},
    ageGroupSeries: [],
    
    genderChartSeries: [],
    genderChartOptions: {
      colors: ['rgba(7, 125, 188, 1)', 'rgba(101, 163, 13, 1)', 'rgba(7, 125, 188, 0.7)'],
    },
    
    tableColumns: [
      { key: 'fullName', label: "Patient Name", sortable: false, },
      { key: 'date_of_birth', label: "Date of Birth", formatter: val => `${this.formatDate(val, 'dd-MM-yy')}`, sortable: false, },
      { key: 'age', sortable: true },
      { key: 'gender', sortable: true, formatter: startCase },
      { key: 'partner', label: "Insurance Partner", sortable: true, },
      { key: 'policy_number', label: "Policy Number", sortable: false, formatter: (val) => val || '-' },
      { key: 'numberOfAppointments', label: "Number of Appointments", sortable: false, },
      { key: 'numberOfConsultations', label: "Number of Consultations", sortable: false, },
      { key: 'numberOfLabRequests', label: "Number of Labs", sortable: false, },
    ],
    
    debounceFetchAllCharts: null,
    debounceFetchListData: null,
  }
},
computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    }
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debounceFetchListData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    
    gender: watchHandlerAndResetPageNumber,
    dateEnd: watchHandlerAndResetPageNumber,
    dateStart: watchHandlerAndResetPageNumber,
    insurance_partner: watchHandlerAndResetPageNumber,

    ageRange: {
      handler(value){
        if (value){
          if (value.includes("+")){
            const [minAge] = value.split("+")
            this.minAge = minAge;
            this.maxAge = "";
          } else {
            const [minAge, maxAge] = value.split("-")
            this.minAge = minAge;
            this.maxAge = maxAge;
          }
        } else {
          this.minAge = "";
          this.maxAge = "";
        }
        this.debounceFetchListData()
        this.debounceFetchAllCharts()
      },
      immediate: false
    },
  },
  created() {
    this.debounceFetchListData = debounce(this.fetchPatientSeen, 500);
    this.debounceFetchAllCharts = debounce(this.fetchAllGraphs, 500);

    this.fetchPatientSeen()
    this.fetchAllGraphs()

    const {
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.currentPage = +page;
    this.perPage = +limit;  
  },
  methods: {
    async fetchAllGraphs() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          gender: this.gender,
          insurance_partner: this.insurance_partner,
          minAge: this.minAge,
          maxAge: this.maxAge,
        }

        const request = await this.useJwt().mainService.adminPatientsSeenReports.fetchPatientSeenGraph(query);

        const { regional_chart_data, age_group_data, gender_chart_data } = request.data.data;
    
        const series = { name: regional_chart_data.name, data: [] }

        series.data = regional_chart_data.data.map(region_data => region_data.count)
        this.chartOptions.xaxis.categories = regional_chart_data.data.map(region_data => {
          const region = region_data._id;
          if (region.includes("Region")){
            return region.replace("Region", "")
          }
          return region_data._id
        })
        this.chartSeries = [series]
        this.regionKey += 1
        if (Array.isArray(age_group_data.data) && age_group_data.data.length){
          this.ageGroupSeries = [{
            name: age_group_data.name,
            data: Object.values(age_group_data.data[0])
          }];
          this.ageGroupOptions = {
            xaxis: {
              categories: Object.keys(age_group_data.data[0])
            },
            colors: chartColors,
          }
        } else {
          this.ageGroupSeries = []
          this.ageGroupOptions = {}
        }
        this.genderChartSeries = gender_chart_data.data.map(gender_data => gender_data.count);
        this.genderChartOptions = {
          labels: gender_chart_data.data.map(gender_data => gender_data._id[0].toUpperCase() + gender_data._id.slice(1))
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchPatientSeen() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          gender: this.gender,
          insurance_partner: this.insurance_partner,
          minAge: this.minAge,
          maxAge: this.maxAge,
          page: this.currentPage,
          limit: this.perPage,
        }

        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.adminPatientsSeenReports.fetchPatientSeen(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.patientsData = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          gender: this.gender,
          insurance_partner: this.insurance_partner,
          minAge: this.minAge,
          maxAge: this.maxAge,
        }

        const response = await this.useJwt().mainService.adminPatientsSeenReports.exportPatientSeen(query);
        FileDownload(response.data, "patients_seen_report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
  }
}
</script>
